import { NewMatchBubble } from "@features/messaging/components/NewMatchBubble";
import type { Match } from "@features/messaging/types";
import { deriveMatchStatus, isChatUnread } from "@features/messaging/utils/matchUtils";

/**
 * Props interface for the NewMatchesSection component.
 */
interface NewMatchesSectionProps {
  /** Array of new matches to display */
  matches: Match[];
  /** Whether the section is loading */
  isLoading: boolean;
  /** Current user ID (for deriving unread state) */
  currentUserId: string;
  /** Callback when a match bubble is clicked */
  onMatchClick: (match: Match) => void;
}

/**
 * NewMatchesSection component for displaying new matches in a horizontal scrollable section.
 * Shows users who liked you back but haven't started chatting yet (or have no messages sent).
 */
export function NewMatchesSection({ matches, isLoading, currentUserId, onMatchClick }: NewMatchesSectionProps) {
  if (!isLoading && (!matches || matches.length === 0)) {
    return null;
  }

  return (
    <div className="bg-white border-b border-gray-200 px-4 py-4" data-testid="new-matches-section">
      {/* Section Title */}
      <h2 className="text-lg font-semibold font-mono text-gray-900 mb-3">New Matches</h2>

      {/* Loading State */}
      {isLoading && (
        <div className="flex gap-3 overflow-x-auto pb-2">
          {[1, 2, 3, 4].map((index) => (
            <div
              key={index}
              className="flex-shrink-0 w-16 h-16 rounded-full bg-gray-200 animate-pulse"
              data-testid="new-match-skeleton"
            />
          ))}
        </div>
      )}

      {/* Matches List */}
      {!isLoading && matches && matches.length > 0 && (
        <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
          {matches.map((match) => (
            <div key={match.user.uid} className="flex flex-shrink-0 flex-col items-center gap-1">
              <NewMatchBubble
                user={match.user}
                isActive={false}
                onClick={() => onMatchClick(match)}
                isNew={deriveMatchStatus(match) === "new" && isChatUnread(match.chat, currentUserId)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
